import { authFetch } from 'src/auth/utils';
import { envVariables } from 'src/common/utils/envVariables';
import qs from 'qs';
import { parseISO } from 'date-fns';
import {
  SortingParam,
  parseSortingParamsToString,
} from 'src/common/components/Filters/constants/sorting';

import {
  PRODUCT_SUMMARY_COLUMN_NAMES_TO_FILTER_MAP,
  ProductSummaryApiSorting,
  ProductSummaryColumnName,
  ProductSummaryListDTO,
} from '../constants/productSummaryConstant';
import { RESPONSE_CODE } from 'src/common/responseCodes';
import { isNil } from 'lodash';

type FetchProductSummaryProps = {
  accountRef?: string;
  page: number;
  pageSize?: number;
  sorting: SortingParam<ProductSummaryColumnName>;
};

export const fetchProductSummary = async (
  { accountRef, page, pageSize, sorting }: FetchProductSummaryProps,
  signal?: AbortSignal,
) => {
  const searchParams: { PageNumber: number; PageSize: number; Order?: string } = {
    PageNumber: page + 1,
    PageSize: pageSize ?? 25,
  };

  if (sorting) {
    const parsedSortings: ProductSummaryApiSorting = {
      sort: sorting.sort,
      columnName: PRODUCT_SUMMARY_COLUMN_NAMES_TO_FILTER_MAP[sorting.columnName],
    };
    searchParams['Order'] = parseSortingParamsToString(parsedSortings);
  }

  const productSummaryResponse = await authFetch(
    `${
      envVariables.VITE_PRODUCTS_USAGE_TRACKING_MODULE_URL
    }/${accountRef}/aggregated?${qs.stringify(searchParams)}`,
    {
      signal,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const productSummaryDTO: ProductSummaryListDTO = await productSummaryResponse.json();

  return {
    totalNumberOfItems: productSummaryDTO.totalProducts,
    items: productSummaryDTO.products.map((row) => ({
      bundleProductCode: row.bundleProductCode,
      activeCount: isNil(row.activeCount) ? '' : row.activeCount.toString(),
      usedData: isNil(row.data.used) ? '' : row.data.used.toString(),
      sent: isNil(row.sms.sent) ? '' : row.sms.sent.toString(),
      usedSeconds: isNil(row.voice.usedSeconds) ? '' : row.voice.usedSeconds.toString(),
    })),
  };
};

export const fetchProductSummaryLastUpdateTime = async (signal?: AbortSignal) => {
  const productSummaryLastUpdateResponse = await authFetch(
    `${envVariables.VITE_PRODUCTS_USAGE_TRACKING_MODULE_URL}/aggregated/last-update-date`,
    {
      signal,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  if (productSummaryLastUpdateResponse.status === RESPONSE_CODE.NO_CONTENT) {
    return null;
  }

  const productSummaryLastUpdateDTO: string = await productSummaryLastUpdateResponse.json();

  return parseISO(productSummaryLastUpdateDTO);
};

export type ProductSummary = Omit<
  Awaited<ReturnType<typeof fetchProductSummary>>,
  'lastUpdatedTimestamp'
> & {
  lastUpdatedTimestamp: null | Date;
};
